import { isDefined } from '@sgme/fp';
import type { BackTestQuoteInfo } from 'apiConnector/back-test';
import { backTestApiConnector } from 'apiConnector/back-test';
import { withAppCrashIfException } from 'epic';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  cancelBackTestFailure,
  cancelBackTestSuccess,
  getBackTest,
  getBackTestFailed,
  getBackTestSucceed,
  runBackTestFailed,
  runBackTestSucceed,
} from 'state/backTest/backTest.actions';
import { getBackTestByTabId } from 'state/backTest/backTest.selector';
import type { EpicWithThunk } from 'state/state.common';
import type { HttpClientError } from 'utils/http/httpClient.model';
import { extractErrorType, extractHttpErrorMessage, extractTimeout } from 'utils/http/httpClient.model';

export function requestBackTestProcessingEpic(): EpicWithThunk {
  return (actions$, state$, { httpClient }) =>
    actions$.pipe(
      ofType('RUN_BACK_TEST'),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const backTest = getBackTestByTabId(state, action.tabId);
        if (backTest === undefined) {
          return EMPTY;
        }
        const { requestBackTest } = backTestApiConnector(httpClient);
        const quotes: BackTestQuoteInfo[] = [
          { id: backTest.quoteIds.initialQuoteId, label: backTest.quoteLabels.initialQuoteLabel },
          isDefined(backTest.quoteIds.secondQuoteId)
            ? {
                id: backTest.quoteIds.secondQuoteId,
                label: backTest.quoteLabels.secondQuoteLabel,
              }
            : undefined,
        ].filter(isDefined);
        return requestBackTest({ quotes, maturities: backTest.maturities }).pipe(
          map(response => runBackTestSucceed(action.tabId, response.backTestId)),
          catchError((err: HttpClientError) =>
            of(
              runBackTestFailed(action.tabId, extractHttpErrorMessage(err), extractTimeout(err), extractErrorType(err)),
            ),
          ),
        );
      }),
    );
}

export function cancelBackTestEpic(): EpicWithThunk {
  return (actions$, state$, { httpClient }) =>
    actions$.pipe(
      ofType('CANCEL_BACK_TEST'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const backTestId = getBackTestByTabId(state, tabId)?.backTestId;
        if (backTestId === undefined) {
          return EMPTY;
        }
        return backTestApiConnector(httpClient)
          .cancelBackTest(backTestId)
          .pipe(
            map(() => cancelBackTestSuccess(tabId)),
            catchError(() => of(cancelBackTestFailure(tabId))),
          );
      }),
    );
}

export function getBackTestResultEpic(): EpicWithThunk {
  return (actions$, _, { httpClient }) =>
    actions$.pipe(
      ofType('GET_BACK_TEST'),
      mergeMap(({ backTestId, tabId }) =>
        backTestApiConnector(httpClient)
          .getBackTest(backTestId)
          .pipe(
            takeUntil(
              actions$.pipe(
                ofType('CANCEL_BACK_TEST'),
                filter(cancel => cancel.tabId === tabId),
              ),
            ),
            map(response => getBackTestSucceed(tabId, response)),
            catchError((err: HttpClientError) =>
              of(getBackTestFailed(tabId, extractHttpErrorMessage(err), extractTimeout(err), extractErrorType(err))),
            ),
          ),
      ),
    );
}
export function onBackTestResultReceivedEpic(): EpicWithThunk {
  return (actions$, _, { streaming }) =>
    actions$.pipe(
      ofType('RUN_BACK_TEST_SUCCEED'),
      mergeMap(runSucceedAction =>
        streaming.backTestMessage$.pipe(
          takeUntil(
            actions$.pipe(
              ofType('CANCEL_BACK_TEST'),
              filter(({ tabId }) => tabId === runSucceedAction.tabId),
            ),
          ),
          ofType('BackTestResult'),
          filter(({ reconciliationId }) => runSucceedAction.backTestId === reconciliationId),
          map(event => getBackTest(runSucceedAction.tabId, event.reconciliationId)),
        ),
      ),
    );
}

export const backTestEpics = () =>
  withAppCrashIfException(
    'backTest',
    combineEpics(
      withAppCrashIfException('requestBackTestProcessingEpic', requestBackTestProcessingEpic()),
      withAppCrashIfException('onBackTestResultReceivedEpic', onBackTestResultReceivedEpic()),
      withAppCrashIfException('getBackTestResultEpic', getBackTestResultEpic()),
      withAppCrashIfException('cancelBackTestEpic', cancelBackTestEpic()),
    ),
  );
